import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useOnboardingStore } from '../../stores/onboarding.store';

export default function OnboardingNavGuard() {
  const isActive = useOnboardingStore((s) => s.isActive);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const mappedTool = useOnboardingStore((s) => s.mappedTool);
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (!isActive) {
      prevPathRef.current = location.pathname;
      return;
    }

    const nextPath = location.pathname;
    const prevPath = prevPathRef.current;

    if (nextPath === prevPath) return;

    // Always allow /app and empathy (needed for AI Buddy exit)
    if (nextPath === '/app' || nextPath === '/app/empathy') {
      prevPathRef.current = nextPath;
      return;
    }

    // During guided-tool step, allow the mapped tool route
    if (currentStep === 'guided-tool' && mappedTool) {
      const toolRoute =
        mappedTool === 'giraffe-translator'
          ? '/app/tools/giraffe-translator'
          : '/app/tools/needs-guesser';
      if (nextPath === toolRoute || nextPath === '/app/tools') {
        prevPathRef.current = nextPath;
        return;
      }
    }

    // During quick-tour, allow navigation when in detail phase (user clicked a tab)
    if (currentStep === 'quick-tour') {
      const phase = useOnboardingStore.getState().quickTourPhase;
      if (phase === 'detail') {
        prevPathRef.current = nextPath;
        return;
      }
      // A warning, not info: it refuses, and it fires only while a tour step
      // is on screen, where info is held back (app/toastConfig.ts).
      toast.warning("Complete this step first, or click 'Skip Tour' to exit.", {
        toastId: 'onboarding-nav-block',
        autoClose: 3000,
      });
      void navigate(prevPath, { replace: true });
      return;
    }

    // For welcome, goal-selection, vulnerability — allow /app but block everything else
    if (['welcome', 'goal-selection', 'warm-response', 'vulnerability'].includes(currentStep)) {
      toast.warning("Complete this step first, or click 'Skip Tour' to exit.", {
        toastId: 'onboarding-nav-block',
        autoClose: 3000,
      });
      void navigate(prevPath, { replace: true });
      return;
    }

    // Default: allow
    prevPathRef.current = nextPath;
  }, [location.pathname, isActive, currentStep, mappedTool, navigate]);

  return null;
}
